import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CalendarClock, RefreshCw } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;
export const GROUPS = [
  { id: "ricerca_visite", label: "Ricerca e visite" },
  { id: "ingressi", label: "Ingressi" },
  { id: "fatturazione", label: "Fatturazione" },
];

export default function PratichePipelineBoard({ headers, onOpen }) {
  const params = new URLSearchParams(window.location.search);
  const [group, setGroup] = useState(params.get("cdrptGruppo") || "ricerca_visite");
  const [quick, setQuick] = useState(params.get("cdrptVista") || "");
  const [data, setData] = useState([]);
  const [fasi, setFasi] = useState({});
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ gruppo: group });
      if (quick) query.set("vista", quick);
      query.set("responsabile_filtro", quick === "mie" ? "mie" : quick === "non_assegnate" ? "non_assegnate" : "tutte");
      const response = await fetch(`${API}/api/admin/leads/pipeline?${query}`, { headers });
      if (!response.ok) throw new Error("Impossibile caricare la pipeline");
      const json = await response.json();
      setData(json.data || []);
      setFasi(json.fasi || {});
      setColumns(json.colonne || []);
      setError("");
    } catch (e) {
      setError(e.message || "Impossibile caricare la pipeline");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [group, quick, headers]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    query.set("cdrptGruppo", group);
    quick ? query.set("cdrptVista", quick) : query.delete("cdrptVista");
    window.history.replaceState({}, "", `${window.location.pathname}?${query}`);
    load();
  }, [group, quick, load]);

  const fallbackColumns = {
    ricerca_visite: ["nuovo", "primo_contatto", "ricerca_strutture", "strutture_proposte", "contatti_visite"],
    ingressi: ["ingresso_programmato", "ingresso_effettivo"],
    fatturazione: ["fatturazione_in_corso", "fatturazione_completata"],
  };
  const visibleColumns = columns.length ? columns : fallbackColumns[group] || [];

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {GROUPS.map((item) => <Button key={item.id} size="sm" variant={group === item.id ? "default" : "outline"} onClick={() => setGroup(item.id)}>{item.label}</Button>)}
        <span className="mx-1 h-5 border-l border-admin-border" />
        {[["", "Tutte"], ["mie", "Le mie pratiche"], ["non_assegnate", "Non assegnate"], ["oggi", "Da fare oggi"], ["scadute", "Scadute"], ["senza_attivita", "Senza prossima attività"], ["da_verificare", "Da verificare"]].map(([id, label]) => (
          <Button key={id} size="sm" variant={quick === id ? "secondary" : "ghost"} onClick={() => setQuick(quick === id ? "" : id)}>{label}</Button>
        ))}
        <Button className="ml-auto" size="sm" variant="ghost" onClick={load} disabled={loading}><RefreshCw className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"} /></Button>
      </div>
      {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error} <Button size="sm" variant="outline" className="ml-2" onClick={load}>Riprova</Button></div> : null}
      {loading ? <p className="text-sm text-admin-muted">Caricamento pipeline…</p> : null}
      <div className="flex snap-x gap-3 overflow-x-auto pb-3">
        {visibleColumns.map((fase) => {
          const rows = data.filter((item) => (item.pratica?.fase || "nuovo") === fase);
          return <div key={fase} className="w-72 shrink-0 snap-start rounded-xl border border-admin-border bg-admin-surface p-2">
            <div className="mb-2 flex items-center justify-between px-1 text-sm font-semibold text-admin-text"><span>{fasi[fase]}</span><Badge variant="secondary">{rows.length}</Badge></div>
            <div className="space-y-2">{rows.length === 0 ? <p className="rounded-lg border border-dashed border-admin-border p-3 text-xs text-admin-muted">Nessuna pratica in questa fase.</p> : rows.map((item) => {
              const next = item.prossima_attivita;
              return <button key={item.id} type="button" onClick={() => onOpen(item.id)} className={`w-full rounded-lg border p-3 text-left shadow-sm transition hover:border-admin-accent ${item.scaduta ? "border-rose-300 bg-rose-50" : "border-admin-border bg-white"}`}>
                <p className="font-semibold text-admin-text">{item.nome_lead || "Richiesta senza referente"}</p>
                <p className="mt-1 text-xs text-admin-muted">{item.city || item.zona_richiesta || "Zona non indicata"} · {item.budget ? `€ ${item.budget}` : "Budget non indicato"}</p>
                <p className="mt-2 text-xs text-admin-muted">{item.pratica?.responsabile?.nome || "Responsabile da assegnare"}</p>
                {next ? <p className={`mt-2 flex gap-1 text-xs ${item.scaduta ? "font-semibold text-rose-700" : "text-admin-muted"}`}>{item.scaduta ? <AlertTriangle className="h-3.5 w-3.5" /> : <CalendarClock className="h-3.5 w-3.5" />}{next.testo} · {new Date(next.scadenza_at).toLocaleString("it-IT")}</p> : <p className="mt-2 text-xs font-medium text-amber-700">Senza prossima attività</p>}
              </button>;
            })}</div>
          </div>;
        })}
      </div>
    </section>
  );
}
